import { useState, useEffect, Suspense } from 'react'
import { NavLink, Outlet, Link, Navigate, useLocation } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  LayoutDashboard, ShoppingBag, Code2, Package,
  ShoppingCart, BookOpen, BarChart2, Settings, Menu, X, ArrowLeft, Wallet,
  ShieldCheck, Loader2, HardDrive, Key, Sparkles, Image, DollarSign, Activity,
  Upload,
} from 'lucide-react'
import { useWallet } from '@/hooks/useWallet'
import { useAuthStore } from '@/stores/authStore'
import { Toaster } from '@/components/ui/Toast'
import { toast } from '@/stores/toastStore'
import { AIAssistant } from '@/components/admin/AIAssistant'
import { useAIProviderStore } from '@/stores/aiProviderStore'

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/products', label: 'Products', icon: ShoppingBag },
  { to: '/admin/drive', label: 'Drive Library', icon: HardDrive },
  { to: '/admin/contracts', label: 'Smart Contracts', icon: Code2 },
  { to: '/admin/bundles', label: 'Bundles', icon: Package },
  { to: '/admin/carousel', label: 'Hero Carousel', icon: Image },
  { to: '/admin/orders', label: 'Orders', icon: ShoppingCart },
  { to: '/admin/treasury', label: 'Treasury', icon: Wallet },
  { to: '/admin/education', label: 'Education', icon: BookOpen },
  { to: '/admin/monitoring', label: 'Monitoring', icon: Activity },
  { to: '/admin/analytics', label: 'Analytics', icon: BarChart2 },
  { to: '/admin/google-setup', label: 'Google Setup', icon: Key },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
  { to: '/admin/pricing', label: 'Pricing', icon: DollarSign },
  { to: '/admin/seller-uploads', label: 'Seller Uploads', icon: Upload },
]

function truncateAddress(addr: string) {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`
}

function Sidebar({ onClose }: { onClose?: () => void }) {
  const { address } = useWallet()

  return (
    <div className="w-[260px] h-full bg-[#060606] border-r border-[#141414] flex flex-col">
      {/* Brand */}
      <div className="px-5 pt-6 pb-5 border-b border-[#141414]">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-[#E8850C] flex items-center justify-center shadow-[0_0_14px_rgba(232,133,12,0.4)]">
              <span className="text-white text-[11px] font-bold leading-none">S6</span>
            </div>
            <div>
              <p className="text-[#E0E0E0] font-bold text-sm tracking-tight leading-none">SaaSFarm</p>
              <p className="text-[#E8850C] text-[9px] font-bold tracking-[0.22em] uppercase mt-0.5">Studio</p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-[#666] hover:text-[#E0E0E0] transition-colors p-0.5"
              aria-label="Close sidebar"
            >
              <X size={17} />
            </button>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-px overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onClose}
            className={({ isActive }) =>
              [
                'flex items-center gap-3 py-2.5 rounded-lg text-sm transition-colors duration-150',
                isActive
                  ? 'border-l-2 border-[#E8850C] pl-[10px] pr-3 text-[#E8850C] bg-[rgba(232,133,12,0.07)]'
                  : 'border-l-2 border-transparent pl-[10px] pr-3 text-white/70 hover:text-white hover:bg-white/5',
              ].join(' ')
            }
          >
            <Icon size={15} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-4 py-4 border-t border-[#141414] space-y-3">
        {address && (
          <div>
            <p className="text-[10px] font-semibold text-[#555] uppercase tracking-widest mb-1">
              Admin Wallet
            </p>
            <p className="text-xs text-[#888] font-mono">{truncateAddress(address)}</p>
          </div>
        )}
        <Link
          to="/"
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs font-medium text-white/70 bg-white/5 border border-white/10 hover:border-[#E8850C] hover:text-white transition-colors"
        >
          <ArrowLeft size={13} />
          Back to Platform
        </Link>
      </div>
    </div>
  )
}

function AdminSignInGate({
  address,
  chainId,
}: {
  address: string
  chainId: number
}) {
  const { login, isLoading } = useAuthStore()
  const [signing, setSigning] = useState(false)
  const [lastError, setLastError] = useState<string | null>(null)

  async function handleSignIn() {
    if (!address) {
      toast.error('No wallet connected', 'Please connect your wallet first.')
      return
    }
    setSigning(true)
    setLastError(null)
    console.log('[AdminSignIn] starting SIWE flow', { address, chainId })
    try {
      const result = await login(address, chainId)
      if (result && 'needsRegistration' in result) {
        // Server didn't recognise this address as admin.
        // Most likely ADMIN_WALLET is not set or doesn't match in .env.local.
        const msg = 'Server did not grant admin session — check that ADMIN_WALLET in .env.local matches this address exactly.'
        console.error('[AdminSignIn] needsRegistration returned — admin wallet mismatch', result)
        setLastError(msg)
        toast.error('Admin wallet not recognised', msg)
      } else {
        console.log('[AdminSignIn] login succeeded')
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not authenticate'
      console.error('[AdminSignIn] login failed:', err)
      setLastError(msg)
      toast.error('Sign-in failed', msg)
    } finally {
      setSigning(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Brand mark */}
        <div className="flex items-center gap-2.5 mb-10 justify-center">
          <div className="w-8 h-8 rounded-lg bg-[#E8850C] flex items-center justify-center shadow-[0_0_18px_rgba(232,133,12,0.4)]">
            <span className="text-white text-xs font-bold leading-none">S6</span>
          </div>
          <div>
            <p className="text-[#F5F5F5] font-bold text-sm tracking-tight leading-none">SaaSFarm</p>
            <p className="text-[#E8850C] text-[9px] font-bold tracking-[0.22em] uppercase mt-0.5">Studio</p>
          </div>
        </div>

        <div className="bg-[#0D0D0D] border border-[#1E1E1E] rounded-2xl p-8">
          <div className="flex flex-col items-center text-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-[rgba(232,133,12,0.1)] border border-[rgba(232,133,12,0.2)] flex items-center justify-center">
              <ShieldCheck size={26} className="text-[#E8850C]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#F5F5F5]">Admin Authentication</h1>
              <p className="text-sm text-[#6B6B6B] mt-1">
                {address
                  ? 'Sign a message with your wallet to activate the admin session.'
                  : 'Connect your admin wallet to continue.'}
              </p>
            </div>
          </div>

          {address ? (
            <>
              <div className="mb-6 px-3 py-2.5 bg-[#111] border border-[#1A1A1A] rounded-lg">
                <p className="text-[10px] text-[#555] uppercase tracking-widest mb-0.5 font-semibold">Connected Wallet</p>
                <p className="text-xs text-[#A0A0A0] font-mono">{address}</p>
              </div>

              {lastError && (
                <div className="mb-4 px-3 py-2.5 bg-[#1a0a0a] border border-[#3d1515] rounded-lg">
                  <p className="text-[10px] text-[#f87171] uppercase tracking-widest mb-0.5 font-semibold">Error</p>
                  <p className="text-xs text-[#f87171] font-mono break-all">{lastError}</p>
                </div>
              )}

              <button
                onClick={handleSignIn}
                disabled={signing || isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#E8850C] hover:bg-[#D4750A] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
              >
                {signing || isLoading ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Waiting for signature…
                  </>
                ) : (
                  <>
                    <ShieldCheck size={15} />
                    Sign in as Admin
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                import('@/stores/modalStore').then(({ useModalStore }) => {
                  useModalStore.getState().open('wallet')
                })
              }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#E8850C] hover:bg-[#D4750A] text-white text-sm font-semibold transition-colors"
            >
              <Wallet size={15} />
              Connect Wallet
            </button>
          )}
        </div>

        <p className="text-center text-xs text-[#444] mt-6">
          {address ? 'This session expires in 24 hours.' : 'You must connect the admin wallet configured on the server.'}
        </p>
      </div>
    </div>
  )
}

export default function AdminLayout() {
  const { address, chainId } = useWallet()
  const { isAuthenticated, isAdmin: serverIsAdmin, isLoading: authLoading, checkSession } = useAuthStore()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [aiInitialPrompt, setAiInitialPrompt] = useState<string | undefined>()
  const location = useLocation()

  // Bootstrap session on cold load. AdminLayout is rendered OUTSIDE the
  // public AppLayout (so the admin UI doesn't carry navbar/footer chrome),
  // which means the App-level checkSession effect never fires for /admin.
  // Without this, the initial isLoading=true state would never resolve and
  // anonymous visitors would see an infinite spinner.
  useEffect(() => {
    void checkSession()
  }, [checkSession])

  // Detect AI providers as soon as admin is authenticated
  const { detect, selectedProvider, loading: aiDetecting } = useAIProviderStore()
  useEffect(() => {
    if (isAuthenticated) detect()
  }, [isAuthenticated, detect])

  // Listen for "ai-assistant:open" events from any admin component
  useEffect(() => {
    function handleAiOpen(e: Event) {
      const detail = (e as CustomEvent).detail as { prompt?: string }
      setAiInitialPrompt(detail?.prompt)
      setAiOpen(true)
    }
    window.addEventListener('ai-assistant:open', handleAiOpen)
    return () => window.removeEventListener('ai-assistant:open', handleAiOpen)
  }, [])

  // Brief check while restoring session from cookie
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 size={22} className="text-[#E8850C] animate-spin" />
      </div>
    )
  }

  // No wallet connected — must connect first
  if (!address) {
    return (
      <>
        <AdminSignInGate address="" chainId={typeof chainId === 'number' ? chainId : 1} />
        <Toaster />
      </>
    )
  }

  // Wallet is connected but no active server session — require SIWE sign-in
  if (!isAuthenticated) {
    return (
      <>
        <AdminSignInGate address={address} chainId={typeof chainId === 'number' ? chainId : 1} />
        <Toaster />
      </>
    )
  }

  // Server confirms this is an admin session
  if (!serverIsAdmin) {
    // User is authenticated but not an admin — redirect to dashboard
    toast.error('Access denied', 'Your wallet does not have admin privileges.')
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div className="min-h-screen flex bg-[#0A0A0A]">
      {/* Desktop sidebar — fixed */}
      <aside className="hidden lg:flex w-[260px] shrink-0 fixed inset-y-0 left-0 z-30">
        <Sidebar />
      </aside>

      {/* Mobile: hamburger */}
      <button
        onClick={() => setDrawerOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-30 p-2 bg-[#141414] border border-[#1E1E1E] rounded-lg text-white/50 hover:text-white transition-colors"
        aria-label="Open menu"
      >
        <Menu size={18} />
      </button>

      {/* Mobile: overlay drawer */}
      {drawerOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="lg:hidden fixed inset-y-0 left-0 z-50 flex">
            <Sidebar onClose={() => setDrawerOpen(false)} />
          </div>
        </>
      )}

      {/* Main content with page transition */}
      <main className="flex-1 lg:ml-[260px] min-h-screen overflow-x-hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="p-6 lg:p-8"
          >
            <Suspense fallback={
              <div className="flex items-center justify-center py-24">
                <Loader2 size={22} className="text-[#E8850C] animate-spin" />
              </div>
            }>
              <Outlet />
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </main>

      {/* AI Agent FAB with provider status */}
      <button
        onClick={() => setAiOpen(true)}
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-2"
        aria-label="Open AI Agent"
      >
        {/* Status pill — shows provider name when detected */}
        {selectedProvider && !aiDetecting && (
          <span className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-[#111] border border-[#1E1E1E] rounded-xl text-[10px] text-[#888] font-medium group-hover:border-[rgba(232,133,12,0.3)] group-hover:text-[#CCC] transition-colors">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            AI Ready
          </span>
        )}
        {aiDetecting && (
          <span className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-[#111] border border-[#1E1E1E] rounded-xl text-[10px] text-[#666] font-medium">
            <Loader2 size={10} className="animate-spin" />
            Detecting...
          </span>
        )}
        <div className="w-12 h-12 rounded-2xl bg-[#E8850C] hover:bg-[#D4750A] text-white flex items-center justify-center shadow-[0_0_24px_rgba(232,133,12,0.35)] hover:shadow-[0_0_32px_rgba(232,133,12,0.5)] transition-all hover:scale-105 active:scale-95 relative">
          <Sparkles size={20} />
          {/* Status dot */}
          {selectedProvider && (
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-[#0A0A0A]" />
          )}
          {!selectedProvider && !aiDetecting && (
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#555] border-2 border-[#0A0A0A]" />
          )}
        </div>
      </button>

      {/* AI Assistant panel */}
      <AIAssistant
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiInitialPrompt(undefined) }}
        initialPrompt={aiInitialPrompt}
      />

      <Toaster />
    </div>
  )
}
